import type { TooltipAlign, TooltipRect, TooltipSide } from './computeTooltipCoords'
import * as React from 'react'
import { computeTooltipCoords } from './computeTooltipCoords'

export function useTooltipPosition(
  open: boolean,
  side: TooltipSide,
  align: TooltipAlign,
  triggerRef: React.RefObject<HTMLElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
) {
  const [coords, setCoords] = React.useState<{ top: number, left: number } | null>(null)

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current
    const content = contentRef.current

    if (!trigger || !content) {
      return
    }

    setCoords(computeTooltipCoords(
      side,
      align,
      trigger.getBoundingClientRect() as TooltipRect,
      content.getBoundingClientRect() as TooltipRect,
    ))
  }, [side, align, triggerRef, contentRef])

  React.useLayoutEffect(() => {
    if (!open) {
      return
    }

    updatePosition()

    let rafId: number | null = null
    const scheduleReposition = () => {
      if (rafId !== null) {
        return
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        updatePosition()
      })
    }

    window.addEventListener('resize', scheduleReposition, { passive: true })
    window.addEventListener('scroll', scheduleReposition, { passive: true, capture: true })

    const content = contentRef.current
    let observer: ResizeObserver | undefined

    if (content && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleReposition)
      observer.observe(content)
    }

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
      window.removeEventListener('resize', scheduleReposition)
      window.removeEventListener('scroll', scheduleReposition, { capture: true })
      observer?.disconnect()
    }
  }, [open, updatePosition, contentRef])

  return coords
}
